import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2 } from 'lucide-react'
import { Voce, VoceUpdate } from '../../types/offerte'

interface SortableVoceRowProps {
  voce: Voce
  disabled: boolean
  onUpdateQuantita: (voceId: string, quantita: number) => void
  onUpdateSconto: (voceId: string, sconto: number) => void
  onUpdateNota: (voceId: string, descrizione: string) => void
  onUpdateVoce: (voceId: string, updates: VoceUpdate) => void
  onDelete: (voceId: string) => void
  formatCurrency: (value: number | string) => string
  integrativaLabel?: string
}

export function SortableVoceRow({
  voce,
  disabled,
  onUpdateQuantita,
  onUpdateSconto,
  onUpdateNota,
  onUpdateVoce,
  onDelete,
  formatCurrency,
  integrativaLabel,
}: SortableVoceRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: voce.id, disabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  const isVoceLibera = voce.tipo === 'voce' && !voce.listino_id

  // Local state per input
  const [localQta, setLocalQta] = useState(String(voce.quantita ?? ''))
  const [localSconto, setLocalSconto] = useState(String(voce.sconto_percentuale || '0'))
  const [localNota, setLocalNota] = useState(voce.descrizione)
  const [localPrezzo, setLocalPrezzo] = useState(String(voce.prezzo_unitario ?? ''))
  const [localDescrizione, setLocalDescrizione] = useState(voce.descrizione)
  const [localUM, setLocalUM] = useState(voce.unita_misura)

  const voceQta = String(voce.quantita ?? '')
  const voceSconto = String(voce.sconto_percentuale || '0')

  if (voce.tipo === 'nota') {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className="bg-amber-50/50 border-l-4 border-amber-300"
      >
        <td className="py-2 px-1 w-8">
          {!disabled && (
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
        </td>
        <td colSpan={6} className="py-2 px-2">
          {disabled ? (
            <span className="text-sm text-gray-700 italic">{voce.descrizione}</span>
          ) : (
            <input
              type="text"
              value={localNota}
              onChange={(e) => setLocalNota(e.target.value)}
              onBlur={() => {
                if (localNota !== voce.descrizione) {
                  onUpdateNota(voce.id, localNota)
                }
              }}
              placeholder="Nota / Sezione..."
              className="w-full px-2 py-1 text-sm italic text-gray-700 bg-transparent border border-transparent hover:border-gray-200 focus:border-primary-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          )}
        </td>
        {!disabled && (
          <td className="py-2 text-right px-2">
            <button
              onClick={() => onDelete(voce.id)}
              className="text-gray-400 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </td>
        )}
      </tr>
    )
  }

  // tipo='voce'
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-gray-50 ${integrativaLabel ? 'bg-orange-50/50 border-l-4 border-orange-400' : isVoceLibera ? 'bg-indigo-50/30' : ''}`}
    >
      <td className="py-3 px-1 w-8">
        {!disabled && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
      </td>
      <td className="py-3">
        <div className="flex items-center gap-1">
          {integrativaLabel && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-bold whitespace-nowrap">{integrativaLabel}</span>
          )}
          {isVoceLibera ? (
            <span className="font-mono text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
              LIBERA
            </span>
          ) : (
            <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
              {voce.codice_listino}
            </span>
          )}
        </div>
      </td>
      <td className="py-3 text-gray-700 text-sm">
        {isVoceLibera && !disabled ? (
          <input
            type="text"
            value={localDescrizione}
            onChange={(e) => setLocalDescrizione(e.target.value)}
            onBlur={() => {
              if (localDescrizione !== voce.descrizione) {
                onUpdateVoce(voce.id, { descrizione: localDescrizione })
              }
            }}
            className="w-full px-2 py-1 text-sm text-gray-700 bg-transparent border border-transparent hover:border-gray-200 focus:border-primary-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        ) : (
          voce.descrizione
        )}
      </td>
      <td className="py-3 text-right">
        {disabled ? (
          <span className="font-medium">{voce.quantita}</span>
        ) : (
          <input
            type="number"
            min="0.01"
            step="0.01"
            data-field={`qta-${voce.id}`}
            value={localQta}
            onChange={(e) => setLocalQta(e.target.value)}
            onBlur={() => {
              const q = parseFloat(localQta)
              if (!isNaN(q) && q > 0) {
                onUpdateQuantita(voce.id, q)
              } else {
                setLocalQta(voceQta)
              }
            }}
            className="w-16 px-2 py-1 text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        )}
      </td>
      <td className="py-3 text-gray-500 text-sm">
        {isVoceLibera && !disabled ? (
          <input
            type="text"
            value={localUM}
            onChange={(e) => setLocalUM(e.target.value)}
            onBlur={() => {
              if (localUM !== voce.unita_misura) {
                onUpdateVoce(voce.id, { unita_misura: localUM })
              }
            }}
            className="w-12 px-1 py-1 text-sm text-center border border-transparent hover:border-gray-200 focus:border-primary-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        ) : (
          voce.unita_misura
        )}
      </td>
      <td className="py-3 text-right text-gray-600 text-sm">
        {isVoceLibera && !disabled ? (
          <input
            type="number"
            min="0"
            step="0.01"
            value={localPrezzo}
            onChange={(e) => setLocalPrezzo(e.target.value)}
            onBlur={() => {
              const p = parseFloat(localPrezzo)
              if (!isNaN(p) && p >= 0) {
                onUpdateVoce(voce.id, { prezzo_unitario: p })
              } else {
                setLocalPrezzo(String(voce.prezzo_unitario ?? ''))
              }
            }}
            className="w-20 px-2 py-1 text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        ) : (
          formatCurrency(voce.prezzo_unitario ?? 0)
        )}
      </td>
      <td className="py-3 text-right">
        {disabled ? (
          voce.sconto_percentuale !== 0 && (
            <span className={`text-sm font-medium ${voce.sconto_percentuale > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {voce.sconto_percentuale > 0 ? `-${voce.sconto_percentuale}%` : `+${Math.abs(voce.sconto_percentuale)}%`}
            </span>
          )
        ) : (
          <input
            type="number"
            step="0.5"
            value={localSconto}
            onChange={(e) => setLocalSconto(e.target.value)}
            onBlur={() => {
              const s = parseFloat(localSconto)
              if (!isNaN(s)) {
                onUpdateSconto(voce.id, s)
              } else {
                setLocalSconto(voceSconto)
              }
            }}
            className="w-16 px-2 py-1 text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        )}
      </td>
      <td className="py-3 text-right font-medium text-gray-900 text-sm">
        {formatCurrency(voce.totale_riga ?? 0)}
      </td>
      {!disabled && (
        <td className="py-3 text-right px-2">
          <button
            onClick={() => onDelete(voce.id)}
            className="text-gray-400 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      )}
    </tr>
  )
}
